import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { usePermission } from '../../../contexts/PermissionContext';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import '../Negotiation.css';
import useNegotiationList from '../hooks/useNegotiationList';
import {
  NEGOTIATION_STATUSES,
  exportNegotiationList,
  getPeriodLabel,
} from '../utils/negotiationList';
import { getStatusMeta } from '../utils/negotiationStatus';
import NegotiationStatusBadge from './NegotiationStatusBadge';

const STAT_CARDS = [
  {
    key: 'total',
    status: '',
    label: 'Total Negotiation',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  ...NEGOTIATION_STATUSES.map(status => ({
    key: status.value,
    status: status.value,
    label: getStatusMeta(status.value).label,
    icon: status.icon,
    tone: status.stat,
  })),
];

const ProductInfo = ({ item }) => {
  const name = item?.productName;
  const sub = item?.productTypeName;
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const StatCard = ({ card, counts, isActive, onSelect }) => {
  const total = counts?.total || 0;
  const count = counts?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

// The list endpoint returns every package, so search, status filter and
// paging all run on the client (see useNegotiationList).
const NegotiationTable = ({
  endpoint = '/negotiation',
  detailBasePath = '/apps-negotiation/dtl',
  mode = 'buyer',
  productTab = 'product-hydro',
  refreshTrigger = 0,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const isSupplier = mode === 'supplier';
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    allItems,
    statusCounts,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    periodFilter,
    periodOptions,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePeriodFilter,
    handlePageChange,
    handlePerRowsChange,
    clearFilters,
  } = useNegotiationList({
    endpoint,
    productTab,
    refreshTrigger,
  });

  const handleExport = format => {
    if (!allItems.length) {
      notify.info(MESSAGES.noDataToExport);
      return;
    }
    setIsExporting(true);
    try {
      exportNegotiationList(allItems, {
        format,
        sheetName: exportName,
        isSupplier,
      });
    } catch (error) {
      notify.apiError(error, 'export negotiation data');
    } finally {
      setIsExporting(false);
    }
  };

  const items = data.items || [];
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const statusOptions = NEGOTIATION_STATUSES.map(status => ({
    value: status.value,
    label: getStatusMeta(status.value).label,
  }));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    statusFilter && {
      key: 'status',
      label: 'Status',
      value: getStatusMeta(statusFilter).label,
      onRemove: () => handleStatusFilter(''),
    },
    periodFilter && {
      key: 'period',
      label: 'Period',
      value: periodFilter,
      onRemove: () => handlePeriodFilter(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  // Route :id is the procurement GUID; the number is display-only.
  const goToDetail = row => navigate(`${detailBasePath}/${row.procurementId}`);

  const emptyState = (
    <EmptyState
      title='No negotiation found'
      emptyHint={
        isSupplier
          ? 'Negotiations on your offers appear here once Pertamina opens them.'
          : 'Negotiations appear here once offers are selected in the Negotiation tab of a saved Tender Evaluation.'
      }
      filteredHint='Nothing matches the current search or filters.'
      hasFilters={hasFilters}
      onClear={clearFilters}
      secondaryAction={
        !isSupplier && can('canRead', '/apps-tenderevaluation/view')
          ? {
              label: 'Go to Tender Evaluation',
              icon: 'ri-arrow-right-line',
              onClick: () => navigate('/apps-tenderevaluation/view'),
            }
          : undefined
      }
    />
  );

  const columns = [
    {
      id: 'procurementNumber',
      name: 'ID Proc',
      selector: row => row.procurementNumber,
      cell: row => (
        <span className='to-id'>{row.procurementNumber || '-'}</span>
      ),
      width: '11%',
      sortable: true,
    },
    {
      id: 'titleProcurement',
      name: 'Procurement',
      selector: row => row.titleProcurement,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productName} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.titleProcurement}>
              {row.titleProcurement || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: isSupplier ? '40%' : '33%',
      sortable: true,
    },
    {
      id: 'period',
      name: 'Period',
      selector: row => getPeriodLabel(row),
      cell: row => getPeriodLabel(row),
      width: '9%',
      sortable: true,
    },
    {
      id: 'productName',
      name: 'Product Type',
      selector: row => row.productName,
      cell: row => (
        <span className='to-product-name'>{row.productName || '-'}</span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'cargoTotal',
      name: 'Cargo Total',
      selector: row => row.cargoTotal,
      cell: row => row.cargoTotal ?? '-',
      width: '9%',
      sortable: true,
    },
    ...(!isSupplier
      ? [
          {
            id: 'partnerTotal',
            name: 'Total Partners',
            selector: row => row.partnerTotal,
            cell: row => row.partnerTotal ?? '-',
            width: '9%',
            sortable: true,
          },
        ]
      : []),
    {
      id: 'status',
      name: 'Status',
      selector: row => row.statusLabel,
      cell: row => <NegotiationStatusBadge code={row.statusLabel} />,
      width: '11%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label={
              row.procurementNumber
                ? `Open detail ${row.procurementNumber}`
                : 'Open detail'
            }
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '6%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        `to-mcard--${getStatusMeta(item.statusLabel).tone}`
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='to-id'>{item.procurementNumber || '-'}</span>
        <NegotiationStatusBadge code={item.statusLabel} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productName} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.titleProcurement || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Period</span>
          <span>{getPeriodLabel(item)}</span>
        </div>
        <div>
          <span>Cargo Total</span>
          <span>{item.cargoTotal ?? '-'}</span>
        </div>
      </div>

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={6} xl={3}>
            <StatCard
              card={card}
              counts={isLoading ? null : statusCounts}
              isActive={statusFilter === card.status}
              onSelect={() =>
                handleStatusFilter(
                  statusFilter === card.status && card.status ? '' : card.status
                )
              }
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={handleSearch}
            placeholder='Search by title, ID Proc, product or period...'
            ariaLabel='Search negotiation'
          />
          <ListFilterSelect
            label='Status'
            value={statusFilter}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={statusOptions}
          />
          <ListFilterSelect
            label='Period'
            value={periodFilter}
            onChange={handlePeriodFilter}
            allLabel='All Period'
            options={periodOptions}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} negotiations`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              <i className='ri-download-2-line me-2' aria-hidden='true' />
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            items.map((item, index) => (
              <MobileCard key={item.procurementId || index} item={item} />
            ))}
          {!isLoading && !items.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            columns={columns}
            data={items}
            pagination={false}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(NegotiationTable);
